import React from 'react';
import { motion } from 'framer-motion';
import { Sparkles, Star } from 'lucide-react';
interface BonusCardProps {
  number: string;
  title: string;
  image?: string;
  description: string;
  value: string;
  originalPrice: string;
}
export function BonusCard({
  number,
  title,
  image,
  description,
  value,
  originalPrice
}: BonusCardProps) {
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} className="group relative bg-white rounded-[2.5rem] p-8 border border-purple-100 hover:border-purple-300 transition-all duration-500 shadow-xl hover:shadow-2xl premium-shadow overflow-visible pt-[10px] pb-[10px]" data-id="element-136">
      {/* ========== PREMIUM GIFT RIBBON / SEAL ========== */}
      <div className="absolute -top-4 left-6 z-20 select-none" data-id="element-137">
        {/* Soft glow halo */}
        <motion.span aria-hidden="true" animate={{
        opacity: [0.35, 0.7, 0.35],
        scale: [1, 1.06, 1]
      }} transition={{
        duration: 3.5,
        repeat: Infinity,
        ease: 'easeInOut'
      }} className="absolute -inset-3 rounded-2xl blur-xl" style={{
        background: 'radial-gradient(ellipse at center, rgba(212,175,55,0.45) 0%, rgba(124,58,237,0.25) 50%, transparent 75%)'
      }} data-id="element-138" />

        <div className="relative" data-id="element-139">
          {/* Ribbon body */}
          <div className="relative pl-4 pr-7 py-2 flex items-center gap-2 shadow-[0_8px_20px_-6px_rgba(76,29,149,0.55)]" style={{
          background: 'linear-gradient(135deg, #2e1065 0%, #4c1d95 35%, #6d28d9 65%, #4c1d95 100%)',
          clipPath: 'polygon(0 0, 100% 0, 92% 50%, 100% 100%, 0 100%, 6% 50%)'
        }} data-id="element-140">
            {/* Inner gold border line */}
            <span aria-hidden="true" className="absolute inset-[3px] pointer-events-none" style={{
            border: '1px solid rgba(253,224,138,0.55)',
            clipPath: 'polygon(0 0, 100% 0, 92% 50%, 100% 100%, 0 100%, 6% 50%)'
          }} data-id="element-141" />

            {/* Sparkle star */}
            <Star size={11} className="text-amber-200 drop-shadow-[0_0_4px_rgba(253,224,138,0.8)] shrink-0" fill="currentColor" strokeWidth={0} data-id="element-142" />

            {/* Label with gold gradient text */}
            <span className="relative font-black uppercase tracking-[0.18em] text-[12px] leading-none whitespace-nowrap" style={{
            background: 'linear-gradient(180deg, #fef3c7 0%, #fde68a 40%, #d4af37 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            fontFamily: "'Cormorant Garamond', serif",
            letterSpacing: '0.22em'
          }} data-id="element-143">
              {number}
            </span>

            {/* Shine sweep across ribbon */}
            <span className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-144">
              <span className="absolute top-0 -left-1/3 h-full w-1/3 skew-x-[-20deg]" style={{
              background: 'linear-gradient(90deg, transparent 0%, rgba(253,224,138,0.45) 50%, transparent 100%)',
              animation: 'mp-shine-sweep 5s ease-in-out infinite'
            }} data-id="element-145" />
            </span>
          </div>

          {/* Folded ribbon tail (left, behind) */}
          <span aria-hidden="true" className="absolute -left-2 top-full -mt-1 w-3 h-3" style={{
          background: 'linear-gradient(135deg, #1e1b4b 0%, #2e1065 100%)',
          clipPath: 'polygon(100% 0, 0 0, 100% 100%)'
        }} data-id="element-146" />
        </div>
      </div>

      {/* Subtle gold corner accent */}
      <span aria-hidden="true" className="absolute top-3 right-4 text-amber-400/60" data-id="element-147">
        <Sparkles size={14} data-id="element-148" />
      </span>

      {image && <div className="mb-8 overflow-hidden rounded-3xl aspect-square shadow-inner bg-purple-50 relative" data-id="element-149">
          <img src={image} alt={title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" data-id="element-150" />
          {/* Soft top vignette to integrate ribbon */}
          <div aria-hidden="true" className="absolute inset-x-0 top-0 h-16 pointer-events-none" style={{
        background: 'linear-gradient(180deg, rgba(255,255,255,0.25) 0%, transparent 100%)'
      }} data-id="element-151" />
        </div>}

      <h3 className="text-2xl font-bold text-purple-950 mb-4 leading-tight font-magical italic" data-id="element-152">
        {title}
      </h3>

      <p className="text-zinc-600 font-medium leading-relaxed pt-[0px] pb-[0px] mb-[0px]" data-id="element-153">
        {description}
      </p>

      <div className="border-t border-purple-50 flex flex-col pt-[0px] gap-[0px]" data-id="element-154">
        <div className="flex items-center justify-between" data-id="element-155">
          <span className="text-zinc-400 font-bold uppercase tracking-[0.15em] text-[10px] opacity-70" data-id="element-156">
            Valor Real:
          </span>
          <span className="text-zinc-400 font-bold text-sm line-through decoration-purple-300/60" data-id="element-157">
            {originalPrice}
          </span>
        </div>
        <div className="flex items-center justify-end" data-id="element-158">
          <span className="font-black text-2xl tracking-tight text-gift-gradient font-magical italic" data-id="element-159">
            {value}
          </span>
        </div>
      </div>
    </motion.div>;
}